import {EventEmitter} from '@angular/core';
import {FormControl, FormGroup, ValidatorFn} from '@angular/forms';

import * as _ from 'lodash';
import {Modernizr} from '../../../vendor/modernizr';
import * as _moment from 'moment';
import {FormData} from './data';
import {
  FormBooleanSchemaData,
  FormDateTimeSchemaData,
  FormFileSchemaData,
  FormGrid,
  FormGridWithRows,
  FormNumberSchemaData,
  FormSchemaActionButton,
  FormSchemaBooleanFunction,
  FormSchemaData,
  FormSchemaDataWithActionButton,
  FormSchemaDataWithIcon,
  FormSchemaEvent,
  FormSchemaEventFunction,
  FormSchemaEvents,
  FormSchemaIcon,
  FormSchemaStringFunction,
  FormSchemaTypes,
  FormSchemaTypeSearch,
  FormSelectSchemaData,
  FormSelectSchemaFormattedOptions,
  FormTextareaSchemaData,
  FormTextSchemaData,
  FormTypeSearchSchemaData,
  FormWysiwygSchemaData,
  HelperSchema,
  ToolbarQuill
} from '../form.interface';
import {FormSchemaValidationMessages, FormSchemaValidations, GetSchemaValidators} from '../form.validators';
import {FormSchemaMask, GetMask} from '../form.mask';
import Quill from 'quill';

/**
 * @ignore
 */
const moment = _moment;

export class FormSchema {
  constructor(
    private _schema: FormSchemaData,
    private _control: FormControl,
    public form: FormData
  ) {
    if ((<FormSelectSchemaData>this._schema).options) {
      this._setFormattedOptions((<FormSelectSchemaData>this._schema).options);
    }

    if (_.isUndefined(this._schema.default_value)) {
      this._schema.default_value = null;
    }

    this.default_value = this._schema.default_value;
    this.setValue(
      _.isUndefined(this._schema.value)
        ? this._default_value
        : this._schema.value
    );
  }

  private _formatted_options: Array<any> = [];
  private _default_value: any;
  private _validators: ValidatorFn[];
  public quillEditor: Quill;

  public onValidate: EventEmitter<any> = new EventEmitter();

  public nativeElement: HTMLElement;
  public component: any;
  public focused = false;
  public custom_error_message: string = null;

  public set key(value: string) {
    this._schema.key = value;
  }

  public get key(): string {
    return this._schema.key ? this._schema.key : null;
  }

  public set type(value: FormSchemaTypes) {
    this._schema.type = value;
  }

  public get type(): FormSchemaTypes {
    return this._schema.type ? this._schema.type : 'text';
  }

  public set group(value: string) {
    this._schema.group = value;
  }

  public get group(): string {
    return this._schema.group ? this._schema.group : null;
  }

  public get field_type(): string {
    switch (this.type) {
      // case 'header':    return 'header';
      case 'checkbox':
        return 'checkbox';
      case 'color':
        return 'color';
      case 'date':
        return 'date';
      case 'file':
        return 'file';
      case 'number':
        return 'number';
      case 'radio':
        return 'radio';
      case 'range':
        return 'range';
      case 'select':
        return 'select';
      case 'textarea':
        return 'textarea';
      case 'texteditor':
        return 'texteditor';
      case 'time':
        return 'time';
      case 'toggle':
        return 'toggle';
      case 'typeahead':
        return 'typeahead';
      case 'wysiwyg':
        return 'wysiwyg';
      default:
        return 'text';
    }
  }

  public set autofocus(value: boolean) {
    this._schema.autofocus = value;
  }

  public get autofocus(): boolean {
    return _.isBoolean(this._schema.autofocus) ? this._schema.autofocus : false;
  }

  public set helper(value: HelperSchema) {
    this._schema.helper = value;
  }

  public get helper() {
    return this._schema.helper;
  }

  public set label(label: string | FormSchemaStringFunction) {
    if (_.isString(label)) {
      this._schema.label = () => <string>label;
    } else if (_.isFunction(label)) {
      this._schema.label = () =>
        (<FormSchemaStringFunction>label)(this, this.form);
    } else {
      this._schema.label = () => null;
    }
  }

  public get label(): string | FormSchemaStringFunction {
    if (_.isString(this._schema.label)) {
      return <string>this._schema.label;
    } else if (_.isFunction(this._schema.label)) {
      return (<FormSchemaStringFunction>this._schema.label)(this, this.form);
    } else {
      return '';
    }
  }

  public set disabled(disabled: boolean | FormSchemaBooleanFunction) {
    if (_.isBoolean(disabled)) {
      this._schema.disabled = () => <boolean>disabled || this.form.locked;
    } else if (_.isFunction(disabled)) {
      this._schema.disabled = () =>
        (<FormSchemaBooleanFunction>disabled)(this, this.form) ||
        this.form.locked;
    } else {
      this._schema.disabled = () => this.form.locked;
    }
  }

  public get disabled(): boolean | FormSchemaBooleanFunction {
    if (_.isBoolean(this._schema.disabled)) {
      return <boolean>this._schema.disabled || this.form.locked;
    } else if (_.isFunction(this._schema.disabled)) {
      return (
        (<FormSchemaBooleanFunction>this._schema.disabled)(this, this.form) ||
        this.form.locked
      );
    } else {
      return this.form.locked;
    }
  }

  public set placeholder(placeholder: string | FormSchemaStringFunction) {
    const schema = <
      | FormTextSchemaData
      | FormTextareaSchemaData
      | FormNumberSchemaData
      | FormDateTimeSchemaData
      >this._schema;

    if (_.isString(placeholder)) {
      schema.placeholder = () => <string>placeholder;
    } else if (_.isFunction(placeholder)) {
      schema.placeholder = () =>
        (<FormSchemaStringFunction>placeholder)(this, this.form);
    } else {
      schema.placeholder = () => null;
    }
  }

  public get placeholder(): string | FormSchemaStringFunction {
    const schema = this._schema as
      | FormTextSchemaData
      | FormTextareaSchemaData
      | FormNumberSchemaData
      | FormDateTimeSchemaData;
    if (_.isString(schema.placeholder)) {
      return <string>schema.placeholder;
    } else if (_.isFunction(schema.placeholder)) {
      return (<FormSchemaStringFunction>schema.placeholder)(this, this.form);
    } else {
      return '';
    }
  }

  public set spinner(value: boolean | FormSchemaBooleanFunction) {
    if (_.isBoolean(value)) {
      (<FormTextSchemaData>this._schema).spinner = () => <boolean>value;
    } else if (_.isFunction(value)) {
      (<FormTextSchemaData>this._schema).spinner = () =>
        (<FormSchemaBooleanFunction>value)(this, this.form);
    } else {
      (<FormTextSchemaData>this._schema).spinner = () => false;
    }
  }

  public get spinner(): boolean | FormSchemaBooleanFunction {
    if (_.isBoolean((<FormTextSchemaData>this._schema).spinner)) {
      return <boolean>(<FormTextSchemaData>this._schema).spinner;
    } else if (_.isFunction((<FormTextSchemaData>this._schema).spinner)) {
      return (<FormSchemaBooleanFunction>(
        (<FormTextSchemaData>this._schema).spinner
      ))(this, this.form);
    } else {
      return false;
    }
  }

  public set color(value: string) {
    (<FormBooleanSchemaData>this._schema).color = value;
  }

  public get color(): string {
    if (this.field_type === 'toggle') {
      return (<FormBooleanSchemaData>this._schema).color
        ? (<FormBooleanSchemaData>this._schema).color
        : 'primary';
    }
    if (this.field_type === 'checkbox') {
      return (<FormBooleanSchemaData>this._schema).color
        ? (<FormBooleanSchemaData>this._schema).color
        : 'primary';
    }
    return null;
  }

  public set step(value: string | number) {
    (<FormNumberSchemaData>this._schema).step = value;
  }

  public get step(): string | number {
    const schema = <FormNumberSchemaData>this._schema;
    return _.isNumber(schema.step) ? schema.step : 1;
  }

  public set mask(value: FormSchemaMask) {
    (<FormTextSchemaData>this._schema).mask = value;
  }

  public get mask(): FormSchemaMask {
    return (<FormTextSchemaData>this._schema).mask;
  }

  public get text_mask(): any {
    return GetMask(this.mask);
  }

  public set grid(grid: FormGrid | FormGridWithRows) {
    this._schema.grid = grid;
  }

  public get grid(): FormGrid | FormGridWithRows {
    const grid = {
      cols:
        this._schema.grid && this._schema.grid.cols
          ? this._schema.grid.cols
          : 12,
      offsets:
        this._schema.grid && this._schema.grid.offsets
          ? this._schema.grid.offsets
          : 0
    };
    if (this.field_type == 'textarea' || this.field_type == 'texteditor') {
      grid['rows'] =
        this._schema.grid && (<FormTextareaSchemaData>this._schema).grid.rows
          ? (<FormTextareaSchemaData>this._schema).grid.rows
          : 2;
    }
    return grid;
  }

  public set css_class(css_class: string | FormSchemaStringFunction) {
    this._schema.css_class = css_class;
  }

  public get css_class(): string | FormSchemaStringFunction {
    if (_.isString(this._schema.css_class)) {
      return this._schema.css_class;
    } else if (_.isFunction(this._schema.css_class)) {
      return (<FormSchemaStringFunction>this._schema.css_class)(
        this,
        this.form
      );
    } else {
      return null;
    }
  }

  public set match_only(match_only: boolean) {
    (<FormTypeSearchSchemaData>this._schema).match_only = match_only;
  }

  public get match_only(): boolean {
    return _.isBoolean((<FormTypeSearchSchemaData>this._schema).match_only)
      ? (<FormTypeSearchSchemaData>this._schema).match_only
      : true;
  }

  public set debounce(debounce: number) {
    (<FormTypeSearchSchemaData>this._schema).debounce = debounce;
  }

  public get debounce(): number {
    const debounce = (<FormTypeSearchSchemaData>this._schema).debounce;
    return _.isNumber(debounce) ? debounce : 300;
  }

  public set(search: FormSchemaTypeSearch) {
    (<FormTypeSearchSchemaData>this._schema).search = search;
  }

  public get search(): FormSchemaTypeSearch {
    return (<FormTypeSearchSchemaData>this._schema).search;
  }

  public set search_list_accessor(search_list_accessor: string) {
    (<FormTypeSearchSchemaData>(
      this._schema
    )).search_list_accessor = search_list_accessor;
  }

  public get search_list_accessor(): string {
    return (
      (<FormTypeSearchSchemaData>this._schema).search_list_accessor || 'label'
    );
  }

  public set search_list_format(search_list_format: (item: any) => string) {
    (<FormTypeSearchSchemaData>(
      this._schema
    )).search_list_format = search_list_format;
  }

  public get search_list_format(): (item: any) => string {
    return (<FormTypeSearchSchemaData>this._schema).search_list_format;
  }

  public set search_value_accessor(search_value_accessor: string) {
    (<FormTypeSearchSchemaData>(
      this._schema
    )).search_value_accessor = search_value_accessor;
  }

  public get search_value_accessor(): string {
    const search_value_accessor = (<FormTypeSearchSchemaData>this._schema)
      .search_value_accessor;
    return _.isString(search_value_accessor) ? search_value_accessor : 'value';
  }

  public set search_value_format(search_value_format: (item: any) => string) {
    (<FormTypeSearchSchemaData>(
      this._schema
    )).search_value_format = search_value_format;
  }

  public get search_value_format(): (item: any) => string {
    return (<FormTypeSearchSchemaData>this._schema).search_value_format;
  }

  public set search_input_accessor(search_input_accessor: string) {
    (<FormTypeSearchSchemaData>(
      this._schema
    )).search_input_accessor = search_input_accessor;
  }

  public get search_input_accessor(): string {
    const search_input_accessor = (<FormTypeSearchSchemaData>this._schema)
      .search_input_accessor;
    return _.isString(search_input_accessor) ? search_input_accessor : 'label';
  }

  public set search_input_format(search_input_format: (item: any) => string) {
    (<FormTypeSearchSchemaData>(
      this._schema
    )).search_input_format = search_input_format;
  }

  public get search_input_format(): (item: any) => string {
    return (<FormTypeSearchSchemaData>this._schema).search_input_format;
  }

  public set search_list(search_list: Array<any>) {
    (<FormTypeSearchSchemaData>this._schema).search_list = search_list;
  }

  public get toolbar(): ToolbarQuill {
    return (<FormWysiwygSchemaData>this._schema).toolbar;
  }

  public set toolbar(toolbar: ToolbarQuill) {
    (<FormWysiwygSchemaData>this._schema).toolbar = toolbar;
  }

  public get search_list(): Array<any> {
    const search_list = (<FormTypeSearchSchemaData>this._schema).search_list;
    return _.isArray(search_list) ? search_list : [];
  }

  public set search_empty_text(
    search_empty_text: string | FormSchemaStringFunction
  ) {
    const schema = <FormTypeSearchSchemaData>this._schema;
    if (_.isString(search_empty_text)) {
      schema.search_empty_text = () => <string>search_empty_text;
    } else if (_.isFunction(search_empty_text)) {
      schema.search_empty_text = () =>
        (<FormSchemaStringFunction>search_empty_text)(this, this.form);
    } else {
      schema.search_empty_text = () => null;
    }
  }

  public get search_empty_text(): string | FormSchemaStringFunction {
    const schema = <FormTypeSearchSchemaData>this._schema;
    if (_.isString(schema.search_empty_text)) {
      return <string>schema.search_empty_text;
    } else if (_.isFunction(schema.search_empty_text)) {
      return (<FormSchemaStringFunction>schema.search_empty_text)(
        this,
        this.form
      );
    } else {
      return 'Nenhum resultado...';
    }
  }

  public set search_on_focus(search_on_focus: boolean) {
    (<FormTypeSearchSchemaData>this._schema).search_on_focus = search_on_focus;
  }

  public get search_on_focus(): boolean {
    const schema = <FormTypeSearchSchemaData>this._schema;
    return _.isBoolean(schema.search_on_focus) ? schema.search_on_focus : false;
  }

  public set select_on_focus(select_on_focus: boolean) {
    (<FormTypeSearchSchemaData>this._schema).select_on_focus = select_on_focus;
  }

  public get select_on_focus(): boolean {
    const schema = <FormTypeSearchSchemaData>this._schema;
    return _.isBoolean(schema.select_on_focus) ? schema.select_on_focus : true;
  }

  public set always_show_list(always_show_list: boolean) {
    (<FormTypeSearchSchemaData>(
      this._schema
    )).always_show_list = always_show_list;
  }

  public get always_show_list(): boolean {
    const schema = <FormTypeSearchSchemaData>this._schema;
    return _.isBoolean(schema.always_show_list)
      ? schema.always_show_list
      : false;
  }

  public set options_label_format(options_label_format: (item: any) => string) {
    (<FormSelectSchemaData>(
      this._schema
    )).options_label_format = options_label_format;
  }

  public get options_label_format(): (item: any) => string {
    return (<FormSelectSchemaData>this._schema).options_label_format;
  }

  public set options_label(options_value: string) {
    (<FormSelectSchemaData>this._schema).options_label = options_value;
  }

  public get options_label(): string {
    return (<FormSelectSchemaData>this._schema).options_label || 'label';
  }

  public set options_value_format(options_value_format: (item: any) => string) {
    (<FormSelectSchemaData>(
      this._schema
    )).options_value_format = options_value_format;
  }

  public get options_value_format(): (item: any) => string {
    return (<FormSelectSchemaData>this._schema).options_value_format;
  }

  public set options_value(options_value: string) {
    (<FormSelectSchemaData>this._schema).options_value = options_value;
  }

  public get options_value(): string {
    const options_value = (<FormSelectSchemaData>this._schema).options_value;
    return _.isString(options_value) ? options_value : 'value';
  }

  public set options(options: Array<any>) {
    (<FormSelectSchemaData>this._schema).options = options;
    this._setFormattedOptions(options);
  }

  public get options(): Array<any> {
    return (<FormSelectSchemaData>this._schema).options || [];
  }

  public get formatted_options(): Array<FormSelectSchemaFormattedOptions> {
    return this._formatted_options;
  }

  public set action_button(value: FormSchemaActionButton) {
    (this._schema as FormSchemaDataWithActionButton).action_button = value;
  }

  public get action_button(): FormSchemaActionButton {
    const schema = this._schema as FormSchemaDataWithActionButton;
    const action_button: FormSchemaActionButton =
      schema.action_button &&
        (schema.action_button.icon || schema.action_button.label)
        ? schema.action_button
        : null;

    if (action_button) {
      if (!action_button.color) {
        action_button.color = 'primary';
      }
      if (!action_button.mode) {
        action_button.mode = 'raised';
      }
      if (!action_button.icon && !action_button.icon_mode) {
        action_button.icon_mode = 'md';
      }
    }

    return action_button;
  }

  public getActionButtonLabel(): string {
    const event: FormSchemaEvent = {
      eventRef: null,
      form: this.form,
      schema: this
    };

    if (_.isString(this.action_button.label)) {
      return <string>this.action_button.label;
    } else if (_.isFunction(this.action_button.label)) {
      return (<FormSchemaEventFunction<FormSchemaEvent, string>>(
        this.action_button.label
      ))(event);
    }

    return '';
  }

  public getActionButtonTitle(): string {
    const event: FormSchemaEvent = {
      eventRef: null,
      form: this.form,
      schema: this
    };

    if (_.isString(this.action_button.title)) {
      return <string>this.action_button.title;
    } else if (_.isFunction(this.action_button.title)) {
      return (<FormSchemaEventFunction<FormSchemaEvent, string>>(
        this.action_button.title
      ))(event);
    }

    return this.getActionButtonLabel();
  }

  public isActionButtonDisabled(): boolean {
    if (this.form.locked) {
      return true;
    }
    if (!this.action_button.disabled) {
      return false;
    }

    const event: FormSchemaEvent = {
      eventRef: null,
      form: this.form,
      schema: this
    };

    if (_.isBoolean(this.action_button.disabled)) {
      return <boolean>this.action_button.disabled;
    } else if (_.isFunction(this.action_button.disabled)) {
      return (<FormSchemaEventFunction<FormSchemaEvent, boolean>>(
        this.action_button.disabled
      ))(event);
    }

    return false;
  }

  public isActionButtonVisible(): boolean {
    const event: FormSchemaEvent = {
      eventRef: null,
      form: this.form,
      schema: this
    };

    if (_.isBoolean(this.action_button.visible)) {
      return <boolean>this.action_button.visible;
    } else if (_.isFunction(this.action_button.visible)) {
      return (<FormSchemaEventFunction<FormSchemaEvent, boolean>>(
        this.action_button.visible
      ))(event);
    }

    return true;
  }

  public actionButtonHandler(eventRef: MouseEvent) {
    if (this.action_button) {
      const _event: FormSchemaEvent = {
        eventRef: eventRef,
        form: this.form,
        schema: this
      };
      this.action_button.onClick(_event);
    }
  }

  public set icon(value: FormSchemaIcon) {
    (this._schema as FormSchemaDataWithIcon).icon = value;
  }

  public get icon(): FormSchemaIcon {
    const schema = this._schema as FormSchemaDataWithIcon;
    const icon: FormSchemaIcon =
      schema.icon && schema.icon.name ? schema.icon : null;
    if (icon) {
      if (!icon.mode) {
        icon.mode = 'md';
      }
      if (!_.isBoolean(icon.highlight_state)) {
        icon.highlight_state = true;
      }
    }
    return icon;
  }

  public set allow_camera(value: boolean) {
    (<FormFileSchemaData>this._schema).allow_camera = value;
  }

  public get allow_camera(): boolean {
    const schema = <FormFileSchemaData>this._schema;
    return _.isBoolean(schema.allow_camera) ? schema.allow_camera : true;
  }

  public isIconVisible(): boolean {
    const event: FormSchemaEvent = {
      eventRef: null,
      form: this.form,
      schema: this
    };

    if (_.isBoolean(this.icon.visible)) {
      return <boolean>this.icon.visible;
    } else if (_.isFunction(this.icon.visible)) {
      return (<FormSchemaEventFunction<FormSchemaEvent, boolean>>(
        this.icon.visible
      ))(event);
    }

    return true;
  }

  public set multiple(value: boolean) {
    (<FormSelectSchemaData>this._schema).multiple = value;
  }

  public get multiple(): boolean {
    return _.isBoolean((<FormSelectSchemaData>this._schema).multiple)
      ? (<FormSelectSchemaData>this._schema).multiple
      : false;
  }

  public get validations(): FormSchemaValidations {
    return this._schema.validations;
  }

  public set validations(validations: FormSchemaValidations) {
    this._schema.validations = validations;
  }

  public get validation_messages(): FormSchemaValidationMessages {
    return this._schema.validation_messages;
  }

  public set validation_messages(messages: FormSchemaValidationMessages) {
    this._schema.validation_messages = messages;
  }

  public get events(): FormSchemaEvents {
    return this._schema.events;
  }

  public set events(events: FormSchemaEvents) {
    this._schema.events = events;
  }

  public set allowNotCropAction(value: boolean) {
    (<FormFileSchemaData>this._schema).allowNotCropAction = value;
  }

  public get allowNotCropAction(): boolean {
    const schema = <FormFileSchemaData>this._schema;
    return _.isBoolean(schema.allowNotCropAction) ? schema.allowNotCropAction : true;
  }

  public set aspectRatio(value: { width: number; height: number; }) {
    (<FormFileSchemaData>this._schema).aspectRatio = value;
  }

  public get aspectRatio(): { width: number; height: number; } {
    const schema = <FormFileSchemaData>this._schema;
    return schema.aspectRatio;
  }

  public set minWidth(value: number) {
    (<FormFileSchemaData>this._schema).minWidth = value;
  }

  public get minWidth(): number {
    const schema = <FormFileSchemaData>this._schema;
    return schema.minWidth || 0;
  }

  public set minHeight(value: number) {
    (<FormFileSchemaData>this._schema).minHeight = value;
  }

  public get minHeight(): number {
    const schema = <FormFileSchemaData>this._schema;
    return schema.minHeight || 0;
  }

  public set zoomable(value: boolean) {
    (<FormFileSchemaData>this._schema).zoomable = value;
  }

  public get zoomable(): boolean {
    const schema = <FormFileSchemaData>this._schema;
    return schema.zoomable == undefined ? true : schema.zoomable;
  }

  public set cropBoxResizable(value: boolean) {
    (<FormFileSchemaData>this._schema).cropBoxResizable = value;
  }

  public get cropBoxResizable(): boolean {
    const schema = <FormFileSchemaData>this._schema;
    return _.isBoolean(schema.cropBoxResizable) ? schema.cropBoxResizable : true;
  }

  public setValue(value: any, ignore_event: boolean = false) {
    if (!this.key || !this._control) {
      return;
    }
    const parsed_value = this._parseSetValue(value);
    const options = {
      emitEvent: !ignore_event,
      emitModelToViewChange: true,
      emitViewToModelChange: !ignore_event
    };
    this._control.setValue(parsed_value, options);
    if (!ignore_event && this.component) {
      this.component.onValueSetted(parsed_value);
    }
  }

  public set value(value: any) {
    this.setValue(value);
  }

  public get value(): any {
    if (!this.key || !this._control) {
      return null;
    }
    return this._parseGetValue(this._control.value);
  }

  public set default_value(value: any) {
    this._default_value = value;
    this._schema.default_value = value;
  }

  public get default_value(): any {
    return this._default_value;
  }

  // Validation getters

  public get min(): string {
    return this._schema.validations && this._schema.validations.min
      ? _.isFunction(this._schema.validations.min)
        ? (<any>this._schema.validations).min()
        : this._schema.validations.min
      : 0;
  }

  public get max(): string {
    return this._schema.validations && this._schema.validations.max
      ? _.isFunction(this._schema.validations.max)
        ? (<any>this._schema.validations).max()
        : this._schema.validations.max
      : null;
  }

  public get minDate(): string {
    return this._schema.validations && this._schema.validations.minDate
      ? _.isFunction(this._schema.validations.minDate)
        ? (<any>this._schema.validations).minDate()
        : this._schema.validations.minDate
      : null;
  }

  public get maxDate(): string {
    return this._schema.validations && this._schema.validations.maxDate
      ? _.isFunction(this._schema.validations.maxDate)
        ? (<any>this._schema.validations).maxDate()
        : this._schema.validations.maxDate
      : null;
  }

  public get minTime(): string {
    return this._schema.validations && this._schema.validations.minTime
      ? _.isFunction(this._schema.validations.minTime)
        ? (<any>this._schema.validations).minTime()
        : this._schema.validations.minTime
      : null;
  }

  public get maxTime(): string {
    return this._schema.validations && this._schema.validations.maxTime
      ? _.isFunction(this._schema.validations.maxTime)
        ? (<any>this._schema.validations).maxTime()
        : this._schema.validations.maxTime
      : null;
  }

  public get minLength(): string {
    return this._schema.validations && this._schema.validations.minLength
      ? _.isFunction(this._schema.validations.minLength)
        ? (<any>this._schema.validations).minLength()
        : this._schema.validations.minLength
      : null;
  }

  public get maxLength(): string {
    return this._schema.validations && this._schema.validations.maxLength
      ? _.isFunction(this._schema.validations.maxLength)
        ? (<any>this._schema.validations).maxLength()
        : this._schema.validations.maxLength
      : null;
  }

  // Custom methods

  public focus() {
    if (this.component && _.isFunction(this.component.focus)) {
      this.component.focus();
    }
  }

  public setCustomError(error: string) {
    this.custom_error_message = error || null;
    this._control.setErrors({custom: !!error});
    if (_.isEmpty(error)) {
      this.update();
    }
    this._setControlTouched();
    this._setControlDirty();
  }

  public setErrors(errors: any) {
    this._control.setErrors(errors);
    if (errors && errors.custom && _.isEmpty(errors.custom)) {
      this.update();
    }
    this._setControlTouched();
    this._setControlDirty();
  }

  public get errors(): any {
    return this.disabled ? null : this._control.errors;
  }

  public get valid(): boolean {
    return this.disabled ? true : this._control.valid;
  }

  public get invalid(): boolean {
    return this.disabled ? false : this._control.invalid;
  }

  public reset() {
    this._control.reset();
    if (this.mask) {
      const mask = this.mask;
      this.mask = null;
      setTimeout(() => (this.mask = mask));
    }
    this.value = this.default_value;
    this.update();
    if (this.component) {
      this.component.onReset();
    }
  }

  public validate(): boolean {
    this._setControlTouched();
    this._setControlDirty();
    this.update();
    this.onValidate.emit(this.valid);

    return this.valid;
  }

  public update() {
    this.updateControlDisabled();
    this._control.updateValueAndValidity({onlySelf: true});
  }

  public inputSupport(type: string): boolean {
    return Modernizr.inputtypes[type];
  }

  public getMask(mask_name: string): any {
    return GetMask(mask_name);
  }

  public updateControlDisabled() {
    if (this.disabled) {
      if (this._control && this._control.enabled) {
        this._control.disable({onlySelf: true});
      }
    } else {
      if (this._control && this._control.disabled) {
        this._control.enable({onlySelf: true});
      }
    }
  }

  public isControlDisabled(): boolean {
    return this._control.disabled;
  }

  public getControl(): FormControl {
    if (!this._validators) {
      this._validators = GetSchemaValidators(this);
    }

    this._control.clearValidators();
    this._control.setValidators(this._validators);
    this._control.updateValueAndValidity({onlySelf: true});

    return this._control;
  }

  private _parseSetValue(value: any): any {
    let parsed_value: any;

    switch (this.type) {
      case 'textarea':
      case 'texteditor':
        parsed_value = _.isString(value) ? value : null;
        break;
      case 'select':
        if (!this.multiple && _.isPlainObject(value)) {
          if (_.isEmpty(this.options_value)) {
            parsed_value = value;
          } else {
            parsed_value = _.at(value, this.options_value)[0];
          }
        } else if (this.multiple && _.isArray(value)) {
          if (_.isEmpty(this.options_value)) {
            parsed_value = value;
          } else {
            parsed_value = value.map(_value => {
              return _.isPlainObject(_value)
                ? _.at(_value, this.options_value)[0]
                : _value;
            });
          }
        } else {
          parsed_value = value;
        }
        break;
      case 'date':
        if (!_.isString(value) && !_.isDate(value)) {
          parsed_value = null;
          break;
        }

        let value_moment;

        if (_.isDate(value)) {
          value_moment = moment(value);
        } else if (_.isString(value)) {
          const pattern1 = /^([0-9]{4})\-(0[1-9]|1[0-2])\-(0[1-9]|[1-2][0-9]|3[0-1])$/;
          const pattern2 = /^(0[1-9]|[1-2][0-9]|3[0-1])\/(0[1-9]|1[0-2])\/([0-9]{4})$/;
          if (pattern1.test(value)) {
            value_moment = moment(value, 'YYYY-MM-DD');
          } else if (pattern2.test(value)) {
            value_moment = moment(value, 'DD/MM/YYYY');
          } else {
            value_moment = moment(Date.parse(value));
          }
        }

        if (this.inputSupport('date')) {
          parsed_value =
            value_moment && value_moment.isValid()
              ? value_moment.format('YYYY-MM-DD')
              : null;
        } else {
          parsed_value =
            value_moment && value_moment.isValid()
              ? value_moment.format('DD/MM/YYYY')
              : null;
        }
        break;
      case 'time':
        const pattern = /^([0-1][0-9]|2[0-3])\:[0-5][0-9](\:[0-5][0-9])?$/;
        if (_.isString(value) && pattern.test(value)) {
          parsed_value = value;
          break;
        } else if (_.isDate(value)) {
          parsed_value = moment(value).format('HH:mm');
          break;
        }

        parsed_value = null;
        break;
      case 'toggle':
      case 'checkbox':
        switch (true) {
          case _.isString(value) && value === 'true':
            parsed_value = true;
            break;
          case _.isString(value) && value === 'false':
            parsed_value = false;
            break;
          case _.isBoolean(value):
            parsed_value = value;
            break;
          default:
            parsed_value = false;
        }
        break;
      case 'number':
        parsed_value = _.isNumber(value)
          ? value
          : _.isString(value) && value
            ? parseFloat(value)
            : null;
        break;
      case 'range':
        parsed_value = _.isNumber(value)
          ? value
          : _.isString(value) && value
            ? parseFloat(value)
            : this.min;
        break;
      case 'text':
      case 'email':
      case 'password':
      case 'url':
        parsed_value = _.isString(value)
          ? value
          : value && _.isFunction(value.toString)
            ? value.toString()
            : null;
        break;
      default:
        parsed_value = value;
    }
    return parsed_value;
  }

  private _parseGetValue(value: any): any {
    switch (this.type) {
      case 'textarea':
      case 'texteditor':
        return _.isString(value) ? value : null;
      case 'select':
        if (this.multiple) {
          return _.isArray(value) ? value : [];
        } else {
          // if (_.isPlainObject(value)) {
          //   const new_value = {};
          //   _.set(new_value, this.options_value, value.value);
          //   _.set(new_value, this.options_label, value.label);
          //   return new_value;
          // }
          return value;
        }
      case 'date':
        if (_.isDate(value)) {
          const value_moment = moment(value);
          return value_moment.isValid()
            ? value_moment.format('YYYY-MM-DD')
            : '';
        } else if (_.isString(value)) {
          const pattern1 = /^([0-9]{4})\-(0[1-9]|1[0-2])\-(0[1-9]|[1-2][0-9]|3[0-1])$/;
          const pattern2 = /^(0[1-9]|[1-2][0-9]|3[0-1])\/(0[1-9]|1[0-2])\/([0-9]{4})$/;
          if (pattern1.test(value)) {
            return moment(value, 'YYYY-MM-DD').format('YYYY-MM-DD');
          } else if (pattern2.test(value)) {
            return moment(value, 'DD/MM/YYYY').format('YYYY-MM-DD');
          } else {
            const value_moment = moment(Date.parse(value));
            if (value_moment.isValid()) {
              return value_moment.format('YYYY-MM-DD');
            }
          }
        }
        return null;
      case 'time':
        const pattern = /^([0-1][0-9]|2[0-3])\:[0-5][0-9](\:[0-5][0-9])?$/;
        if (_.isString(value) && pattern.test(value)) {
          return value;
        } else if (_.isDate(value)) {
          return moment(value).format('HH:mm');
        }
        return null;
      case 'toggle':
      case 'checkbox':
        if (_.isBoolean(value)) {
          return value;
        }
        return false;
      case 'number':
        return _.isString(value) || _.isNumber(value) ? value : null;
      case 'range':
        return _.isString(value) || _.isNumber(value) ? value : this.min;
      case 'text':
      case 'email':
      case 'password':
      case 'url':
        return _.isString(value) ? value : null;
      case 'typeahead':
        let formatted_value = null;
        if (_.isPlainObject(value) || _.isArray(value)) {
          if (_.isFunction(this.search_value_format)) {
            formatted_value = this.search_value_format(value);
          } else if (_.isEmpty(this.search_value_accessor)) {
            formatted_value = value;
          } else {
            const _value_accessor = _.at(value, this.search_value_accessor)[0];
            if (_value_accessor) {
              formatted_value = _value_accessor;
            }
          }
        } else {
          formatted_value = value;
        }
        return formatted_value;
      default:
        return value;
    }
  }

  private _setFormattedOptions(options: Array<any>) {
    this.value = null;
    if (!_.isArray(options)) {
      options = [];
    }

    this._formatted_options = options.map(option => {
      let value = option;
      if (_.isFunction(this.options_value_format)) {
        value = this.options_value_format(option);
      } else if (!_.isEmpty(this.options_value)) {
        value = _.at(option, this.options_value)[0];
      }
      const label = this.options_label_format
        ? this.options_label_format(option)
        : this.options_label
          ? _.at(option, this.options_label)[0]
          : option;
      return {
        value: value,
        label: label
      };
    });
  }

  private _setControlTouched() {
    this._control.markAsTouched({onlySelf: true});
  }

  private _setControlDirty() {
    this._control.markAsDirty({onlySelf: true});
  }

  getQuillInstance() {
    return this.quillEditor;
  }
}
